"use client";

import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { excursionTerms } from "@/lib/excursionTerms";
export default function TermsConditions() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLElement>({
    threshold: 0.1,
    once: true
  });
  return <section id="terms-conditions" data-nav="light" ref={ref} className="bg-background text-foreground py-16 md:py-24 border-b border-border transition-opacity duration-700 ease-out" style={{
    opacity: isVisible ? 1 : 0
  }}>
      <div className="max-w-[1136px] mx-auto px-6">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-8">
          {/* Main content spans columns 3 to 10 (centered readable column) */}
          <div className="md:col-start-3 md:col-span-8">
            {/* Header */}
            <div className="mb-8">
              <h1 className="font-default text-3xl sm:text-4xl md:text-5xl font-bold tracking-tight text-foreground mb-4">
                Terms &amp; Conditions
              </h1>

              {/* Signature hairline rule capped with turquoise terminal dot */}
              <div className="relative flex items-center mb-6" aria-hidden="true">
                <div className="h-px bg-border flex-1" />
                <div className="size-2 rounded-full bg-primary shrink-0 -ml-1" />
              </div>

              <p className="text-sm font-medium text-muted-foreground">
                Last Updated: 18 October 2026
              </p>
              <p className="text-sm font-medium text-muted-foreground mt-1">
                Operator: Ahmed Kamal Hussein Abdel-Aal.
              </p>
            </div>

            {/* Continuous Legal Text */}
            <div className="space-y-6 text-base sm:text-lg leading-relaxed text-foreground/90">
              <p className="text-foreground">
                Welcome to Hurghada Transfer Hub. By making a transfer booking through our website, phone, or WhatsApp, you agree to comply with and be bound by the following terms and operational conditions.
              </p>

              <div className="rounded-md border border-border bg-card p-6 md:p-8 space-y-6 shadow-xs">
                <article className="space-y-4">
                  <h2 className="text-base sm:text-lg font-semibold text-foreground">
                    1. Booking &amp; Confirmation
                  </h2>
                  <div className="space-y-2">
                    <h3 className="text-sm sm:text-base font-semibold text-foreground">
                      Transfers
                    </h3>
                    <p className="text-muted-foreground text-sm sm:text-base leading-relaxed">
                      Bookings made online or via messaging are confirmed once dispatch reviews and issues an official confirmation voucher. We reserve the right to decline bookings where vehicle capacity or route feasibility is unavailable.
                    </p>
                  </div>
                  <div className="space-y-2 pt-2 border-t border-border/50">
                    <h3 className="text-sm sm:text-base font-semibold text-foreground">
                      Excursions
                    </h3>
                    <p className="text-muted-foreground text-sm sm:text-base leading-relaxed">
                      Your excursion booking is confirmed once we receive your chosen payment: either the 50% online card deposit or the full online card payment.
                    </p>
                  </div>
                </article>

                <div className="h-px bg-border" aria-hidden="true" />

                <article className="space-y-4">
                  <h2 className="text-base sm:text-lg font-semibold text-foreground">
                    2. Pricing &amp; Payment
                  </h2>
                  <div className="space-y-2">
                    <h3 className="text-sm sm:text-base font-semibold text-foreground">
                      Transfers
                    </h3>
                    <p className="text-muted-foreground text-sm sm:text-base leading-relaxed">
                      Quoted rates are fixed per vehicle and cover all highway tolls, parking costs, and driver fees. Payment is collected in cash directly by the chauffeur upon arrival at the destination. We accept payment in any currency; please confirm the currency and agreed amount with us before your transfer.
                    </p>
                  </div>
                  <div className="space-y-3 pt-2 border-t border-border/50">
                    <h3 className="text-sm sm:text-base font-semibold text-foreground">
                      Excursions
                    </h3>
                    <p className="text-muted-foreground text-xs sm:text-sm">
                      {excursionTerms.en.scope}
                    </p>
                    <div className="space-y-1.5">
                      <p className="text-sm font-medium text-foreground">
                        {excursionTerms.en.stepsTitle}:
                      </p>
                      <ol className="list-decimal pl-5 space-y-1.5 text-muted-foreground text-sm sm:text-base leading-relaxed">
                        {excursionTerms.en.steps.map((step, idx) => <li key={idx}>
                            <span>{step}</span>
                          </li>)}
                      </ol>
                    </div>
                    <div className="space-y-1 pt-1">
                      <p className="text-sm font-medium text-foreground">
                        {excursionTerms.en.paymentTitle}
                      </p>
                      <p className="text-muted-foreground text-sm sm:text-base leading-relaxed">
                        {excursionTerms.en.paymentOptions}
                      </p>
                    </div>
                    <div className="space-y-1 pt-1">
                      <p className="text-sm font-medium text-foreground">
                        {excursionTerms.en.pickupTitle}
                      </p>
                      <p className="text-muted-foreground text-sm sm:text-base leading-relaxed">
                        {excursionTerms.en.pickupCoverage}
                      </p>
                    </div>
                    <p className="text-muted-foreground text-xs sm:text-sm pt-1">
                      {excursionTerms.en.guideLanguages}
                    </p>
                  </div>
                </article>

                <div className="h-px bg-border" aria-hidden="true" />

                <article className="space-y-2">
                  <h2 className="text-base sm:text-lg font-semibold text-foreground">
                    3. Flight Delays &amp; Waiting Time
                  </h2>
                  <p className="text-muted-foreground text-sm sm:text-base leading-relaxed">
                    We monitor your flight&apos;s arrival time and any delays free of charge. Your transfer includes up to 60 minutes of free waiting time from the agreed meeting time. Providing your flight number when booking helps us monitor your flight effectively.
                  </p>
                </article>

                <div className="h-px bg-border" aria-hidden="true" />

                <article className="space-y-4">
                  <h2 className="text-base sm:text-lg font-semibold text-foreground">
                    4. Cancellations &amp; Amendments
                  </h2>
                  <div className="space-y-2">
                    <h3 className="text-sm sm:text-base font-semibold text-foreground">
                      Transfers
                    </h3>
                    <p className="text-muted-foreground text-sm sm:text-base leading-relaxed">
                      Cancellation is free of charge at least 24 hours before the agreed pickup or meeting time. For cancellations made less than 24 hours before that time, a cancellation fee of 25% of the total agreed booking price applies. Please contact dispatch directly over WhatsApp for urgent changes.
                    </p>
                  </div>
                  <div className="space-y-2 pt-2 border-t border-border/50">
                    <h3 className="text-sm sm:text-base font-semibold text-foreground">
                      Excursions
                    </h3>
                    <div className="space-y-2">
                      <p className="text-xs sm:text-sm font-medium text-foreground">
                        {excursionTerms.en.cancellationTitle}
                      </p>
                      <p className="text-muted-foreground text-sm sm:text-base leading-relaxed">
                        {excursionTerms.en.cancellation}
                      </p>
                    </div>
                    <div className="space-y-1 pt-1">
                      <p className="text-xs sm:text-sm font-medium text-foreground">
                        {excursionTerms.en.refundTitle}
                      </p>
                      <p className="text-muted-foreground text-sm sm:text-base leading-relaxed">
                        {excursionTerms.en.weatherRefund}
                      </p>
                    </div>
                  </div>
                </article>

                <div className="h-px bg-border" aria-hidden="true" />

                <article className="space-y-2">
                  <h2 className="text-base sm:text-lg font-semibold text-foreground">
                    5. Luggage &amp; Vehicle Compliance
                  </h2>
                  <p className="text-muted-foreground text-sm sm:text-base leading-relaxed">
                    Passengers must ensure their party and luggage volume do not exceed the vehicle limits specified in their booking category. We reserve the right to refuse boarding or require a larger supplementary vehicle if luggage limits are exceeded.
                  </p>
                </article>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>;
}